import { request } from "../../api";

export type DeskProvider = "zendesk" | "intercom" | "salesforce" | "freshdesk" | "servicenow";

export interface SupportDesk {
  id: string;
  orgId: string;
  provider: DeskProvider;
  name: string;
  enabled: boolean;
  config: Record<string, string>;
  secretsSet: string[];
  webhookUrl?: string;
  lastNoteAt?: string;
  lastError?: string;
  createdAt: string;
}

export interface DeskInput {
  provider: DeskProvider;
  name: string;
  enabled?: boolean;
  config?: Record<string, string>;
  secrets?: Record<string, string>;
}

const org = (id: string) => `?org=${encodeURIComponent(id)}`;

export const desksApi = {
  list: (o: string) => request<SupportDesk[]>(`/v1/support-desks${org(o)}`),
  create: (o: string, body: DeskInput) => request<SupportDesk>(`/v1/support-desks${org(o)}`, { method: "POST", body }),
  patch: (o: string, id: string, body: Partial<DeskInput>) => request<SupportDesk>(`/v1/support-desks/${encodeURIComponent(id)}${org(o)}`, { method: "PATCH", body }),
  remove: (o: string, id: string) => request<void>(`/v1/support-desks/${encodeURIComponent(id)}${org(o)}`, { method: "DELETE" }),
};

export const DESK_TYPES: Array<{ id: DeskProvider; label: string; description: string }> = [
  { id: "zendesk", label: "Zendesk", description: "Add the session to a new ticket." },
  { id: "intercom", label: "Intercom", description: "Add the session to a new conversation." },
  { id: "salesforce", label: "Salesforce", description: "Add the session to a new case." },
  { id: "freshdesk", label: "Freshdesk", description: "Add the session to a new ticket." },
  { id: "servicenow", label: "ServiceNow", description: "Add the session to a new incident." },
];

export const DESK_SETUP: Record<DeskProvider, { where: string; body?: string }> = {
  zendesk: {
    where: "In Zendesk, add a webhook that runs when a ticket is created and posts to this address.",
    body: `{"ticketId":"{{ticket.id}}","email":"{{ticket.requester.email}}"}`,
  },
  freshdesk: {
    where: "In Freshdesk, add an automation that runs when a ticket is created and calls this address.",
    body: `{"ticketId":"{{ticket.id}}","email":"{{ticket.contact.email}}"}`,
  },
  intercom: {
    where: "In Intercom, add a webhook for new conversations that posts to this address.",
  },
  salesforce: {
    where: "In Salesforce, add a flow on a new case that posts to this address.",
    body: `{"caseId":"{!$Record.Id}","email":"{!$Record.ContactEmail}"}`,
  },
  servicenow: {
    where: "In ServiceNow, add a flow on a new incident that posts to this address. The note is added as a work note.",
    body: `{"incidentId":"{{sys_id}}","email":"{{caller_id.email}}"}`,
  },
};
